// pivotRegistry.ts — Cross-app pivot definitions and URL builder per navigation-map.md §9
//
// Each pivot produces documented URL pattern, opens in new tab, includes return context,
// and emits audit row via appendAuditRow. If destination app is absent, still renders
// documented URL and records destination readiness as explicit result-note item.

export type PivotId = 
  | 'asset-view'           // GVW-039: Map asset → AssetView
  | 'geoconverter-upload'  // LayerPanel Add layer → GeoConverter
  | 'diagram-workspace'    // GVW-067: BIM tag → Diagram Workspace (P&ID)
  | 'plant-registers'      // GVW-067: BIM tag → Plant Engineering registers
  | 'reports-export'       // GVW-114: F7 export → Reports
  | 'admin-integrations'   // Permission denied/credentials → Admin
  | 'ai-studio-model'      // Copilot explain model → AI Studio
  | 'external-schedule'    // Timeline event → external schedule
  | 'drillhole-collar'     // Drillhole collar → Drillhole sub-app
  | 'geoscience-workspace'// Anomaly/interpretation → Geoscience workspaces
  | 'pointcloud-viewer'   // Point cloud tools → Point Cloud Viewer
  | 'edms-document'       // Document marker → EDMS
  | 'admin-audit';        // GVW-133: Full audit → app-platform-admin

export interface PivotContext {
  /** Selected asset/object/tag/document ID */
  resourceId?: string;
  /** Current time for temporal context */
  time?: string;
  /** Current scenario for comparison context */
  scenario?: string;
  /** Current camera position/bounds */
  camera?: Record<string, unknown>;
  /** Return URL state for back navigation */
  returnView?: string;
  /** Additional context specific to pivot type */
  extra?: Record<string, unknown>;
}

export interface PivotDefinition {
  /** Unique identifier */
  id: PivotId;
  /** Human-readable name for UI */
  name: string;
  /** Target application identifier */
  targetApp: string;
  /** Whether this pivot opens external URLs vs internal app URLs */
  isExternal: boolean;
  /** Expected availability - used for result-note tracking */
  expectedAvailable: boolean;
  /** URL pattern template */
  urlPattern: string;
  /** Audit event kind for logging */
  auditKind: string;
}

// Registry of all supported pivots per navigation-map.md §9
export const PIVOT_REGISTRY: Record<PivotId, PivotDefinition> = {
  'asset-view': {
    id: 'asset-view',
    name: 'Open in AssetView',
    targetApp: 'assetview',
    isExternal: false,
    expectedAvailable: true,
    urlPattern: '/<launcher>/assetview/asset/{resourceId}?ret={returnView}',
    auditKind: 'pivot.asset_view',
  },
  
  'geoconverter-upload': {
    id: 'geoconverter-upload',
    name: 'Add via GeoConverter',
    targetApp: 'geoconverter',
    isExternal: false,
    expectedAvailable: true,
    urlPattern: '/<launcher>/geoconverter/upload?return=geoviewer&collection={extra.collectionId}',
    auditKind: 'pivot.geoconverter_upload',
  },
  
  'diagram-workspace': {
    id: 'diagram-workspace',
    name: 'Open in Diagram Workspace',
    targetApp: 'diagram-workspace',
    isExternal: false,
    expectedAvailable: true,
    urlPattern: '/<launcher>/diagram-workspace/tag/{resourceId}?from=geoviewer',
    auditKind: 'pivot.diagram_workspace',
  },
  
  'plant-registers': {
    id: 'plant-registers',
    name: 'View in Registers',
    targetApp: 'registers',
    isExternal: false,
    expectedAvailable: true,
    urlPattern: '/<launcher>/registers/equipment/{resourceId}?from=geoviewer',
    auditKind: 'pivot.plant_registers',
  },
  
  'reports-export': {
    id: 'reports-export',
    name: 'Generate Report',
    targetApp: 'reports',
    isExternal: false,
    expectedAvailable: true,
    urlPattern: '/<launcher>/reports/template/{extra.templateId}?evidence={extra.evidencePackId}',
    auditKind: 'pivot.reports_export',
  },
  
  'admin-integrations': {
    id: 'admin-integrations',
    name: 'Admin Settings',
    targetApp: 'admin',
    isExternal: false,
    expectedAvailable: true,
    urlPattern: '/<launcher>/admin/integrations/{extra.providerId}?from=geoviewer',
    auditKind: 'pivot.admin_integrations',
  },
  
  'ai-studio-model': {
    id: 'ai-studio-model',
    name: 'Explain in AI Studio',
    targetApp: 'ai-studio',
    isExternal: false,
    expectedAvailable: false, // May be filtered in defence variant
    urlPattern: '/<launcher>/ai-studio/model/{resourceId}?audit={extra.auditRowId}',
    auditKind: 'pivot.ai_studio_model',
  },
  
  'external-schedule': {
    id: 'external-schedule',
    name: 'View in Schedule System',
    targetApp: 'external-schedule',
    isExternal: true,
    expectedAvailable: false, // External dependency
    urlPattern: '{extra.externalUrl}', // Full external URL
    auditKind: 'pivot.external_schedule',
  },
  
  'drillhole-collar': {
    id: 'drillhole-collar',
    name: 'Open Drillhole Details',
    targetApp: 'drillhole',
    isExternal: false,
    expectedAvailable: true,
    urlPattern: '/<launcher>/drillhole/{resourceId}?from=geoviewer',
    auditKind: 'pivot.drillhole_collar',
  },
  
  'geoscience-workspace': {
    id: 'geoscience-workspace',
    name: 'Open in Geoscience Workspace',
    targetApp: 'geoscience',
    isExternal: false,
    expectedAvailable: true,
    urlPattern: '/<launcher>/geoscience/{resourceId}?from=geoviewer',
    auditKind: 'pivot.geoscience_workspace',
  },
  
  'pointcloud-viewer': {
    id: 'pointcloud-viewer',
    name: 'Advanced Point Cloud Tools',
    targetApp: 'pointcloud-viewer',
    isExternal: false,
    expectedAvailable: true,
    urlPattern: '/<launcher>/pointcloud-viewer/{resourceId}?from=geoviewer',
    auditKind: 'pivot.pointcloud_viewer',
  },
  
  'edms-document': {
    id: 'edms-document',
    name: 'Open Document',
    targetApp: 'edms',
    isExternal: true,
    expectedAvailable: false, // External system availability varies
    urlPattern: '{extra.documentUrl}', // Full external URL
    auditKind: 'pivot.edms_document',
  },
  
  'admin-audit': {
    id: 'admin-audit',
    name: 'Open Full Audit',
    targetApp: 'admin',
    isExternal: false,
    expectedAvailable: true,
    urlPattern: '/<launcher>/admin/audit?row={resourceId}',
    auditKind: 'pivot.admin_audit',
  },
};

/**
 * Build a pivot URL from definition and context.
 * Returns the final URL with all placeholders replaced.
 */
export function buildPivotUrl(pivotId: PivotId, context: PivotContext = {}): string {
  const definition = PIVOT_REGISTRY[pivotId];
  if (!definition) {
    throw new Error(`Unknown pivot ID: ${pivotId}`);
  }

  let url = definition.urlPattern;
  
  // Replace placeholders with context values
  if (context.resourceId) {
    url = url.replace(/\{resourceId\}/g, encodeURIComponent(context.resourceId));
  }
  
  if (context.returnView) {
    url = url.replace(/\{returnView\}/g, encodeURIComponent(context.returnView));
  }
  
  // Handle extra context placeholders - special handling for external URLs
  if (context.extra) {
    Object.entries(context.extra).forEach(([key, value]) => {
      const placeholder = `{extra.${key}}`;
      if (typeof value === 'string' && url.includes(placeholder)) {
        // Don't encode external URLs that are meant to be used as-is
        const shouldEncode = !value.startsWith('http');
        url = url.replace(new RegExp(placeholder.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), shouldEncode ? encodeURIComponent(value) : value);
      }
    });
  }
  
  // Replace launcher placeholder with current launcher base
  // In a real implementation, this would read from runtime config
  const launcherBase = ((globalThis as { __GV_LAUNCHER_BASE__?: string }).__GV_LAUNCHER_BASE__ || '/app')
    .replace(/\/+$/, '');
  // URL patterns include `/<launcher>/...`; replacing the whole segment
  // prevents double slashes when launcherBase itself starts with `/`.
  url = url.replace(/\/<launcher>/g, launcherBase);
  
  return url;
}

/**
 * Get pivot definition by ID.
 */
export function getPivotDefinition(pivotId: PivotId): PivotDefinition {
  const definition = PIVOT_REGISTRY[pivotId];
  if (!definition) {
    throw new Error(`Unknown pivot ID: ${pivotId}`);
  }
  return definition;
}

/**
 * Check if a pivot is available in the current environment.
 * Returns false for external pivots where destination readiness is unknown.
 */
export function isPivotAvailable(pivotId: PivotId): boolean {
  const definition = PIVOT_REGISTRY[pivotId];
  if (!definition) return false;
  
  // Defence variant filtering - AI Studio not available in defence clusters
  if (pivotId === 'ai-studio-model') {
    const isDefenceVariant = (globalThis as { __GV_DEFENCE_VARIANT__?: boolean }).__GV_DEFENCE_VARIANT__ || false;
    if (isDefenceVariant) return false;
  }
  
  // External systems have unknown availability
  if (definition.isExternal) return definition.expectedAvailable;
  
  return definition.expectedAvailable;
}

/**
 * Get all pivot definitions, optionally filtered by availability.
 */
export function getAllPivots(onlyAvailable = false): PivotDefinition[] {
  const pivots = Object.values(PIVOT_REGISTRY);
  if (!onlyAvailable) return pivots;
  
  return pivots.filter(pivot => isPivotAvailable(pivot.id));
}